import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import MobilePopup from '../newPopup/mobilePopup/MobilePopup';
import Btn from '../button/btn/Btn';
import SendingTabs from './components/sendingTabs/SendingTabs';
import WhatsAppTab from './components/whatsAppTab/WhatsAppTab';
import SmsTab from './components/smsTab/SmsTab';
import EmailTab from './components/emailTab/EmailTab';
import SuccessTab from './components/successTab/SuccessTab';
import SenderIcon from './icons/SenderIcon';
import UserIcon from './icons/UserIcon';
import SendingPopupModals from './components/sendingPopupModals/SendingPopupModals';
import * as css from './SendingPopup.css';
import {useMessageContent} from './hooks/useMessageContent';
import {SendingPopupProps, TabType} from './types';
import {useSendingLogic} from './hooks/useSendingLogic';
import CustomScrollbar from '../customScrollbar/CustomScrollbar';
import zIndex from '../../globalVariables/zIndex';
import {TState} from '../../redux/store.type';
import RunningButton from '../../pages/components/runningButton/RunningButton';
import Loader from '../loader/Loader';
import useGlobalShow from '../../hooks/useGlobalShow';
import {getPackageProducer} from '../../redux/account/accountActions';
import {getAccountSettings} from '../../redux/account/accountActions';
import {IS_NEW_TEMPLATE} from '../../config/config';
import {
    SendingPopupAiGeneratorButton,
    SendingPopupAiGeneratorPanel,
    useSendingPopupAiGenerator,
} from './components/sendingPopupAiGenerator/SendingPopupAiGenerator';

function SendingPopup({show, whatsappProps, smsProps, emailProps, amount, amountText, variablesType, allowedList, initialTab}: SendingPopupProps) {
    const {t} = useTranslation('mailing');
    const {t: tWhatsApp} = useTranslation('whatsapp');
    const senderEmail = useSelector((state: TState) => state.account.packagesProducer?.brandingEmail);
    const senderSms = useSelector((state: TState) => state.account.packagesProducer?.brandingSms);
    const isBranding = useSelector((state: TState) => state.account.packagesProducer?.branding);
    const getEventSettingsXHR = useSelector((state: TState) => state.event.getEventSettingsXHR);
    const getPackageProducerXHR = useSelector((state: TState) => state.account.getPackageProducerXHR);
    const getAccountSettingsXHR = useSelector((state: TState) => state.account.getAccountSettingsXHR);
    const account = useSelector((state: TState) => state.account);
    const eid = useSelector((state: TState) => state.event.eid);
    const trialPopupShow = useGlobalShow('isOpenTrialPopup');
    const sendApprovalShow = useGlobalShow('isOpenSendApproval');
    const whatsappEditorMode = useSelector((s: TState) => {
        if(!whatsappProps.mailingSource) return null;
        return s.ui.whatsappEditorMode?.[whatsappProps.mailingSource];
    });
    const {currentTab, sendingTab, handleTabChange, handleSave, handleSend, handleActualSend, handleTrialSend, sendingXHR, saveXHR, filteredAllowedList} = useSendingLogic({
        whatsappProps, 
        smsProps, 
        emailProps,
        amount,
        onHasEnoughCredits: () => sendApprovalShow.open(),
        allowedList,
        initialTab,
    });
    const isFreeWhatsApp = currentTab === 'whatsapp' && whatsappEditorMode === 'free';
    const showAiGenerator = currentTab === 'whatsapp' && IS_NEW_TEMPLATE(Number(eid));
    const {aiGeneratorShow, messageType, openAiGenerator, handleUpdateMessage} = useSendingPopupAiGenerator({
        mailingSource: whatsappProps.mailingSource,
        variablesType,
        saveText: whatsappProps.saveText,
    });
    const {getMessageContent} = useMessageContent({currentTab: sendingTab || currentTab, whatsappProps, smsProps, emailProps, variablesType});    
    const refContent = useRef(null);

    const renderTabContent = () => {
        switch (currentTab) {
            case 'whatsapp':
                return (
                    <WhatsAppTab
                        {...whatsappProps}
                        toolbarLeadingExtra={showAiGenerator ? (
                            <SendingPopupAiGeneratorButton onClick={openAiGenerator} />
                        ) : null}
                    />
                );
            case 'sms':
                return <SmsTab {...smsProps} />;
            case 'email':
                return <EmailTab {...emailProps} />;
            default:
                return null;
        }
    };
    const cssStyle = `
        inset-inline-end: 5px;
        inset-block-start: 30px;
        z-index: ${zIndex.baseHigh}
    `;
    useEffect(() => {
        if(!getPackageProducerXHR) {
            getPackageProducer().then();
        }
    }, []);
    useEffect(() => {
        if(account.pid && getAccountSettingsXHR !== 'SUCCESS') {
            (async () => await getAccountSettings())();
        }
    }, [account.pid]);  
    return (
        <>
            <MobilePopup
                close={show.close}
                showClass={show.showClass}
                title={currentTab === 'success' ? t('sendSuccessTitle') : t(`${currentTab}SendTitle`)}
                dataCyName="sendingLead"
                list={undefined}
                forwardRef={undefined}
                TopComponent={null}
            >
                <css.running>
                    <RunningButton />
                </css.running>
                {currentTab === 'success' ? 
                    <SuccessTab currentTab={sendingTab as TabType} amount={amount}
                        messageContent={getMessageContent()} close={show.close} /> :                  
                    <>
                        <SendingTabs
                            currentTab={currentTab}
                            onTabClick={handleTabChange}
                            allowedList={filteredAllowedList}
                        />
                        <CustomScrollbar contentRef={refContent} cssStyle={cssStyle} maxBottom={65}/>
                        <css.tabContent ref={refContent} >
                            <css.editorHeader>
                                <UserIcon />
                                <css.headerText>| {t('to')}:
                                    <css.messagesAmount>
                                        {amount && <css.numbers>{amount}</css.numbers>}
                                        {t(amountText)}
                                    </css.messagesAmount>
                                </css.headerText>
                            </css.editorHeader>
                            <Loader XHR={getEventSettingsXHR} height={undefined}>{renderTabContent()}</Loader>
                        </css.tabContent>
                        <css.actions>
                            <css.senderInfo>
                                {(currentTab !== 'whatsapp' && !!isBranding) &&  <>
                                    <css.senderIcon>
                                        <SenderIcon />
                                    </css.senderIcon>
                                    | {t('theSender')} {currentTab === 'email' ? senderEmail || '' : senderSms || ''}
                                </>}
                            </css.senderInfo>
                            <css.actionButtons>
                                {!isFreeWhatsApp && <Btn onClick={() => trialPopupShow.open()} className="white">
                                    {tWhatsApp('sendTrial')}
                                </Btn>} 
                                <Btn onClick={handleSave} className={`white ${saveXHR ?? ''}`}>
                                    {isFreeWhatsApp ? tWhatsApp('parseTemplateAndSave') : t('save')}
                                </Btn>
                                {!isFreeWhatsApp && <Btn onClick={handleSend} className={sendingXHR}>
                                    {t('sendAndSave')}
                                </Btn>}
                            </css.actionButtons>
                        </css.actions>
                        <SendingPopupModals
                            currentTab={currentTab}
                            amount={amount || 1}
                            messageContent={getMessageContent()}
                            onTrialSend={handleTrialSend}
                            onActualSend={handleActualSend}
                            sendingXHR={sendingXHR}
                        />
                        {showAiGenerator && (
                            <SendingPopupAiGeneratorPanel
                                show={aiGeneratorShow}
                                messageType={messageType}
                                onUpdateMessage={handleUpdateMessage}
                                feature={currentTab}
                            />
                        )}
                    </>
                }
            </MobilePopup>
        </>
    );
}

export default SendingPopup;
